<template>
  <AppShell v-if="showShell">
    <router-view v-slot="{ Component }">
      <Transition
        :css="false"
        @enter="onPageEnter"
        @leave="onPageLeave">
        <component :is="Component" :key="$route.path" />
      </Transition>
    </router-view>
  </AppShell>
  <router-view v-else />

  <div
    v-if="ready && !showShell"
    class="absolute right-3"
    :style="{ top: topOverlayOffset }">
    <LangSelect />
  </div>
</template>

<script lang="ts">
import { i18n } from '@/locales'
import { useAccountStore } from '@/stores/accountStore'
import { useAuthStore } from '@/stores/authStore'
import { useUserStore } from '@/stores/userStore'
import { useAppLockStore } from '@/stores/appLockStore'
import { useNavigationTransitionStore } from '@/stores/navigationTransitionStore'
import { animatePageEnter, animatePageLeave } from '@/globals/typescript/utils/navAnimations'
import type { TLabCustomerLocale } from '@team-milva/lab-models/language'
import { isLabCustomerCompanyUser } from '@team-milva/lab-models/user'
import { mapActions, mapState } from 'pinia'
import { App as CapApp } from '@capacitor/app'
import { Capacitor, type PluginListenerHandle } from '@capacitor/core'

export default {
  name: 'App',
  data() {
    return {
      appStateListener: null as PluginListenerHandle | null,
    }
  },
  computed: {
    ...mapState(useUserStore, ['currentUser']),
    ...mapState(useAuthStore, ['ready']),
    ...mapState(useAccountStore, ['availableLanguages', 'labAccount', 'customerAccount']),
    ...mapState(useNavigationTransitionStore, ['direction']),
    topOverlayOffset(): string {
      return 'calc(0.5rem + env(safe-area-inset-top))'
    },
    showShell(): boolean {
      const isAppRoute = !!this.$route.meta.requiresAuth
      return isAppRoute && !!this.currentUser && !this.$route.meta.hideShell
    },
  },
  watch: {
    availableLanguages(
      newLangs: ReturnType<typeof useAccountStore>['availableLanguages']
    ) {
      const supportedLocales = newLangs.map(
        (l) => l.locale
      ) as TLabCustomerLocale[]

      const currentLocale = i18n.global.locale.value as TLabCustomerLocale
      if (supportedLocales.includes(currentLocale)) return

      // The language in use dropped out of what the account allows (the lab narrowed it while the
      // user was in the app). A signed-in business or insurance user (MIL-402) moves to the account
      // default and it is saved; anyone else gets the first language still on offer — never a hard-coded one, since
      // English may be exactly what was removed.
      const applyFirstSupportedLocale = (): void => {
        const fallbackLocale = supportedLocales[0]
        if (fallbackLocale) {
          i18n.global.locale.value = fallbackLocale
          localStorage.setItem('lang', fallbackLocale)
        }
      }

      if (isLabCustomerCompanyUser(this.currentUser)) {
        // reconcile is a no-op when the saved language is still allowed but the one in use is not
        // (e.g. a stale 'lang' in localStorage), so fall back afterwards if it left it unsupported.
        void this.reconcileLanguageWithAccount(this.labAccount, this.customerAccount).then(() => {
          if (!supportedLocales.includes(i18n.global.locale.value as TLabCustomerLocale)) {
            applyFirstSupportedLocale()
          }
        })
        return
      }

      applyFirstSupportedLocale()
    },
  },
  methods: {
    ...mapActions(useAppLockStore, ['lock']),
    ...mapActions(useUserStore, ['reconcileLanguageWithAccount']),

    onPageEnter(el: Element, done: () => void): void {
      animatePageEnter(el as HTMLElement, done, this.direction)
    },
    onPageLeave(el: Element, done: () => void): void {
      animatePageLeave(el as HTMLElement, done, this.direction)
    },

    // MIL-127 — app-lock. `visibilitychange` is the web-standard signal for
    // backgrounding — used on web, where `@capacitor/app` isn't available.
    onVisibilityChange(): void {
      if (document.visibilityState === 'hidden') {
        this.lock()
      }
    },
  },
  async mounted() {
    if (Capacitor.isNativePlatform()) {
      // `@capacitor/app`'s native appStateChange event is the reliable signal
      // for backgrounding on iOS/Android — visibilitychange doesn't fire
      // consistently in a native WKWebView/Android WebView.
      this.appStateListener = await CapApp.addListener('appStateChange', ({ isActive }) => {
        if (!isActive) this.lock()
      })
    } else {
      document.addEventListener('visibilitychange', this.onVisibilityChange)
    }
  },
  unmounted() {
    this.appStateListener?.remove()
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
  },
}
</script>
